import { useCallback, useEffect, useRef, useState } from 'react';
import { generateAdHocTryOn, generateTryOn, listGenerations } from '../lib/api';
import type { GenerationSummary } from '../lib/types';
import { loadResultsForVisit, resultId, saveResult } from '../lib/results-db';
import { beginCriticalWork, endCriticalWork } from '../pwa/registerSW';
import { presentReason, TRANSPORT_FAILURE, type ReasonPresentation } from './errors';

/**
 * How many generations may be in flight at once.
 *
 * The server permits more — its lock is keyed on member + garment + photo, so a
 * device can legitimately run several garments in parallel. The cap exists for
 * three local reasons: each result is ~1MB held in memory, the rail stops being
 * readable past a handful of cards, and every parallel run holds its own quota
 * reserve, so an enthusiastic customer tapping twenty tiles is twenty Gemini
 * bills before anyone notices the number moving.
 */
export const MAX_CONCURRENT = 5;

export type EntryStatus = 'queued' | 'generating' | 'done' | 'failed';

/**
 * What a queue entry is generating against — the catalogue, or a garment
 * photographed at the counter for this visit only.
 *
 * ⚠️ **`name` means different things on the two arms and both are the label.**
 * On the catalogue arm it is the garment's real name and the customer may
 * recognise it; on the ad-hoc arm it is whatever the salesman typed, or a
 * fallback, and the server discards it entirely. Neither ever affects what is
 * generated — the prompt comes from the category on both paths.
 */
export type GenerationTarget =
  | { kind: 'catalogue'; garmentId: string; name: string }
  | { kind: 'adhoc'; garmentKey: string; category: string; name: string };

/**
 * The identity of the thing being tried on, as one opaque string.
 *
 * ⚠️ **The two arms cannot collide**, which is what makes one string safe: a
 * catalogue id is a UUID and an ad-hoc key is an R2 path beginning `garments/`.
 * This mirrors the server exactly — its in-flight lock puts the `garmentKey` in
 * the same position the `garmentId` occupies, for the same reason.
 */
export function subjectOf(target: GenerationTarget): string {
  return target.kind === 'catalogue' ? target.garmentId : target.garmentKey;
}

export interface GenerationEntry {
  /**
   * `${photoKey}::${subjectOf(target)}`.
   *
   * ⚠️ **Not the garment id.** Entries used to key on the garment alone, which
   * meant that after a retake every garment already tried was permanently
   * un-tappable — `start` refuses a garment that already has a non-failed entry,
   * and the old result satisfied that test forever. Including the photo makes
   * "this garment on the new photo" a different entry, so the rail can hold both
   * and the customer can compare them. It also mirrors the server's lock, which
   * has always been member + garment + **photo**.
   */
  key: string;
  /**
   * ⚠️ **The garment id for a catalogue entry, the `garmentKey` for an ad-hoc
   * one** — `subjectOf(target)`, kept as its own field because it is what
   * `entryKey` and `resultId` are built from and both want a plain string.
   *
   * It is also what goes into IndexedDB's `garmentId` column, which is why that
   * column needs no change: it has only ever been an opaque identity, and the
   * two kinds cannot produce the same value.
   */
  garmentId: string;
  garmentName: string;
  photoKey: string;
  /** Which endpoint this entry generates through, and what it needs to send. */
  target: GenerationTarget;
  status: EntryStatus;
  /** Object URL for a completed result. Revoked when the queue is cleared. */
  imageUrl?: string;
  failure?: ReasonPresentation;
}

interface QueueApi {
  entries: GenerationEntry[];
  /** True while any generation is in flight — drives the wake lock. */
  busy: boolean;
  /**
   * Queues a generation. Takes the whole target rather than a garment, so an
   * ad-hoc entry carries the category it needs all the way to the request —
   * nothing here reaches back for it later.
   */
  start: (target: GenerationTarget) => void;
  retry: (key: string) => void;
  /** Drops in-memory state. Persistence is cleared by `endVisit`, not here. */
  reset: () => void;
  /** The entry for this garment **on the current photo**, if any. */
  entryFor: (garmentId: string) => GenerationEntry | undefined;
}

/**
 * How often the recovery read re-asks while a restored row is still in flight.
 *
 * Deliberately slower than the server's own 3s poll of FASHN: this is a second
 * observer of the same generation, and nothing it learns a second sooner changes
 * what the customer sees.
 */
const RECOVERY_POLL_MS = 3_000;

/**
 * How long recovery keeps asking before calling a still-`running` row lost.
 *
 * ⚠️ **Sized off the server's deadline rather than guessed.** A generation
 * cannot outlive `GEMINI_TIMEOUT_MS` (120s) plus the margin the in-flight lock's
 * TTL already allows, so past this the row is settled and this device simply
 * never saw it settle. Raising the server deadline without raising this leaves a
 * card that gives up while the generation it describes is still running.
 */
const RECOVERY_WINDOW_MS = 140_000;

export function entryKey(photoKey: string, garmentId: string): string {
  return `${photoKey}::${garmentId}`;
}

export function useGenerationQueue(visitId: string | null, photoKey: string | null): QueueApi {
  const [entries, setEntries] = useState<GenerationEntry[]>([]);

  /**
   * ⚠️ **The ref is the source of truth, and React state is the mirror — not the
   * other way round.** It used to be `entriesRef.current = entries` on every
   * render, which meant the ref only caught up when React re-rendered.
   *
   * That was a real double-spend bug, not a tidiness issue. `runNext` marks an
   * entry `generating` and then calls itself to fill the remaining slots in the
   * same tick; with a render-time mirror, that recursive call re-read the *old*
   * array, found the very entry it had just claimed still sitting at `queued`,
   * and dispatched it again — recursing until `runningRef` hit `MAX_CONCURRENT`.
   * One tap on Generate produced **five** POSTs: one took the server's in-flight
   * lock and four came back `already_generating`, which then overwrote the card
   * the first one was legitimately filling.
   *
   * Writing through `commit` keeps the ref correct *synchronously*, so a claim
   * made earlier in a tick is visible to everything later in it.
   */
  const entriesRef = useRef<GenerationEntry[]>([]);

  const commit = useCallback((next: GenerationEntry[]) => {
    entriesRef.current = next;
    setEntries(next);
  }, []);

  const runningRef = useRef(0);

  /**
   * Keys restored from the server as still `generating`.
   *
   * ⚠️ **Only these are re-checked, and only these are given up on.** An entry
   * this device is genuinely running has its own held-open request and resolves
   * itself; timing it out from here would fail a card whose image is seconds
   * away. The set is what keeps recovery from touching live work.
   */
  const recoveredRunningRef = useRef<Set<string>>(new Set());
  const objectUrlsRef = useRef<string[]>([]);
  const photoKeyRef = useRef<string | null>(photoKey);
  photoKeyRef.current = photoKey;
  const visitIdRef = useRef<string | null>(visitId);
  visitIdRef.current = visitId;

  const update = useCallback(
    (key: string, patch: Partial<GenerationEntry>) => {
      commit(
        entriesRef.current.map((entry) => (entry.key === key ? { ...entry, ...patch } : entry)),
      );
    },
    [commit],
  );

  /**
   * Rehydrate the rail after a reload or an iOS cold start, from **two sources
   * that answer different questions.**
   *
   *   IndexedDB  what this device finished and kept — durable, offline, a blob
   *   the server  what actually happened, including what this device never heard
   *
   * ⚠️ Keyed on the **visit**, not the photo — so a retake re-renders everything
   * generated earlier in the visit rather than an empty rail.
   *
   * ⚠️ **Local first, and local wins on any key both hold.** A local entry is a
   * blob that works offline and never expires; the server's is a FASHN url good
   * for ~3 days. Letting the remote copy overwrite a local one would trade a
   * durable image for a borrowed one for no gain.
   *
   * The server's real job is the rows local storage has **no** copy of — the
   * generation that completed after this device stopped listening. That is the
   * whole point: nothing server-side aborts on a client disconnect, so the image
   * was produced and paid for and is simply waiting to be asked for.
   */
  useEffect(() => {
    if (!visitId) {
      commit([]);
      return;
    }
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    const recoveryStartedAt = Date.now();

    const mergeLocal = (stored: Awaited<ReturnType<typeof loadResultsForVisit>>) => {
      if (stored.length === 0) return;
      const restored = stored.map<GenerationEntry>((row) => {
        const url = URL.createObjectURL(row.image);
        objectUrlsRef.current.push(url);
        return {
          key: entryKey(row.photoKey, row.garmentId),
          garmentId: row.garmentId,
          garmentName: row.garmentName,
          photoKey: row.photoKey,
          /**
           * ⚠️ Reconstructed from `adHocCategory` being present, which is the
           * only thing distinguishing the two kinds on disk. A restored entry is
           * always `done` and therefore never generates again — `start` refuses
           * a non-failed entry and `retry` refuses anything but a failed one —
           * so this target is a description rather than something that will be
           * sent. It is reconstructed faithfully anyway: a target that is only
           * *nearly* right is the kind of thing a later change starts trusting.
           */
          target:
            row.adHocCategory === undefined
              ? { kind: 'catalogue', garmentId: row.garmentId, name: row.garmentName }
              : {
                  kind: 'adhoc',
                  garmentKey: row.garmentId,
                  category: row.adHocCategory,
                  name: row.garmentName,
                },
          status: 'done',
          imageUrl: url,
        };
      });
      const known = new Set(entriesRef.current.map((entry) => entry.key));
      commit([...restored.filter((entry) => !known.has(entry.key)), ...entriesRef.current]);
    };

    /**
     * Fold the server's account of the visit into whatever the rail already has.
     *
     * ⚠️ **Never downgrades an entry that is already `done`.** Local storage and
     * a live in-memory result both outrank this, so a completed card keeps the
     * image it is showing.
     */
    const mergeServer = (rows: GenerationSummary[]) => {
      const byKey = new Map(entriesRef.current.map((entry) => [entry.key, entry]));
      const additions: GenerationEntry[] = [];

      for (const row of rows) {
        const key = entryKey(row.photoKey, row.subject);
        const existing = byKey.get(key);

        /**
         * ⚠️ **The label, and the one field the two arms source differently.**
         * The server joins a catalogue garment's name live from `garments` and
         * stores an ad-hoc one, so `name` is already "whatever this card should
         * be captioned". The fallback covers a catalogue garment deleted since.
         */
        const label = row.name ?? 'Garment';

        /**
         * ⚠️ Rebuilt faithfully rather than approximately — on the ad-hoc arm
         * these three values exist **nowhere else**, and a retry sends them
         * straight back. A target that is only nearly right is what turns a
         * recovered card into a second upload and a second charge.
         */
        const target: GenerationTarget =
          row.kind === 'adhoc'
            ? {
                kind: 'adhoc',
                garmentKey: row.subject,
                category: row.category ?? '',
                name: label,
              }
            : { kind: 'catalogue', garmentId: row.subject, name: label };

        if (existing) {
          // Already finished locally — the durable copy stands.
          if (existing.status === 'done') continue;

          if (row.status === 'completed' && row.image) {
            update(key, { status: 'done', imageUrl: row.image, failure: undefined });
          } else if (row.status === 'failed' && existing.status !== 'failed') {
            update(key, {
              status: 'failed',
              failure: row.reason ? presentReason(row.reason) : TRANSPORT_FAILURE,
            });
          }
          continue;
        }

        additions.push({
          key,
          garmentId: row.subject,
          garmentName: label,
          photoKey: row.photoKey,
          target,
          /**
           * ⚠️ A `running` row restores as `generating`, **not** `queued`.
           * `runNext` dispatches anything `queued`, so restoring it that way
           * would fire a second generation against a request the server is still
           * holding — billing twice for one card. `generating` is inert here and
           * is resolved by the re-check below.
           */
          status:
            row.status === 'completed' ? 'done' : row.status === 'failed' ? 'failed' : 'generating',
          ...(row.status === 'completed' && row.image ? { imageUrl: row.image } : {}),
          ...(row.status === 'failed'
            ? { failure: row.reason ? presentReason(row.reason) : TRANSPORT_FAILURE }
            : {}),
        });
      }

      for (const entry of additions) {
        if (entry.status === 'generating') recoveredRunningRef.current.add(entry.key);
      }

      if (additions.length > 0) {
        commit([...additions, ...entriesRef.current]);
      }
    };

    /**
     * Ask the server, then keep asking **only while something it told us about
     * is still in flight.**
     *
     * ⚠️ **This is the one place the PWA polls, and it is bounded on purpose.**
     * The happy path never reaches here: a generation the device is waiting on
     * arrives on its own held-open request. This runs only after a reload found
     * a row mid-flight, which is a request whose client is already gone.
     *
     * ⚠️ **Failures are swallowed to nothing.** Offline, or an older API with no
     * such route, must leave the rail exactly as local storage rebuilt it —
     * recovery is a bonus on top of what the device already had, never a
     * precondition for showing it.
     */
    const recover = async () => {
      try {
        const { generations } = await listGenerations(visitId);
        if (cancelled) return;
        mergeServer(generations);
      } catch {
        // Offline, or a server that predates this route. The local copy stands.
        return;
      }

      if (cancelled) return;

      const stillRunning = entriesRef.current.some(
        (entry) => recoveredRunningRef.current.has(entry.key) && entry.status === 'generating',
      );

      if (!stillRunning) return;

      /**
       * ⚠️ **Bounded by the server's own deadline, not by a guess.** A
       * generation cannot outlive `GEMINI_TIMEOUT_MS` (120s) plus the margin the
       * lock TTL already allows, so past this window the row is settled and this
       * device simply never saw it. Spinning for ever would be worse than
       * offering the retry.
       */
      if (Date.now() - recoveryStartedAt > RECOVERY_WINDOW_MS) {
        for (const entry of entriesRef.current) {
          if (recoveredRunningRef.current.has(entry.key) && entry.status === 'generating') {
            update(entry.key, { status: 'failed', failure: TRANSPORT_FAILURE });
          }
        }
        return;
      }

      pollTimer = setTimeout(() => void recover(), RECOVERY_POLL_MS);
    };

    void loadResultsForVisit(visitId)
      .then((stored) => {
        if (!cancelled) mergeLocal(stored);
      })
      .catch(() => {
        // IndexedDB unavailable (private window, evicted). The server read below
        // is then the only source, which is exactly when it matters most.
      })
      .finally(() => {
        if (!cancelled) void recover();
      });

    return () => {
      cancelled = true;
      clearTimeout(pollTimer);
    };
  }, [visitId, commit, update]);

  /**
   * How long a card refused with `already_generating` keeps spinning before it
   * is called a failure the customer can act on.
   *
   * Sized against the other side: a generation runs ~10s and the server's lock
   * carries a 70s TTL, so anything still refused past this either belongs to
   * somebody else's device or is a lock that outlived its request. Either way
   * the honest thing is to stop spinning and offer the retry.
   */
  const STALE_LOCK_MS = 75_000;

  const staleTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const scheduleStaleLockCheck = useCallback(
    (key: string) => {
      const timers = staleTimersRef.current;
      clearTimeout(timers.get(key));
      timers.set(
        key,
        setTimeout(() => {
          timers.delete(key);
          const entry = entriesRef.current.find((item) => item.key === key);
          // Only if it never moved on — the real generation may well have
          // landed in the meantime, and overwriting a finished card would be
          // worse than the spinner ever was.
          if (entry?.status !== 'generating') return;
          update(key, {
            status: 'failed',
            failure: {
              message: 'This one is taking longer than expected — try again.',
              kind: 'error',
              retryable: true,
            },
          });
        }, STALE_LOCK_MS),
      );
    },
    [update],
  );

  // Timers outlive the request that set them, so they have to be cleared on
  // unmount or a torn-down screen fires `update` into nothing.
  useEffect(() => {
    const timers = staleTimersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const runNext = useCallback((): void => {
    if (runningRef.current >= MAX_CONCURRENT) return;
    const next = entriesRef.current.find((entry) => entry.status === 'queued');
    if (!next) return;

    const currentVisitId = visitIdRef.current;
    if (!currentVisitId) return;

    runningRef.current += 1;
    // Holds off any pending service-worker update until this returns — an
    // update-triggered reload here would kill a paid generation.
    beginCriticalWork();
    update(next.key, { status: 'generating', failure: undefined });

    void (async () => {
      try {
        /**
         * ⚠️ The entry's own `photoKey`, not the session's current one. A retake
         * mid-flight would otherwise generate a queued entry against the new
         * photo while the rail labels it as the old one.
         */
        /**
         * ⚠️ **Two endpoints, one queue.** Everything either side of this call —
         * the concurrency cap, the stale-lock timer, the failure presentation,
         * the rail card — is deliberately shared, because an ad-hoc generation
         * costs the same money and takes the same minute. The target is the only
         * thing that differs, and it decides only which url the request goes to.
         */
        const response =
          next.target.kind === 'catalogue'
            ? await generateTryOn({
                photoKey: next.photoKey,
                garmentId: next.target.garmentId,
                /**
                 * Sent so the server can record the generation under this visit
                 * and hand it back if this connection dies — see
                 * `listGenerations`. Optional server-side, so an older API
                 * simply ignores it.
                 */
                visitId: currentVisitId,
              })
            : await generateAdHocTryOn({
                photoKey: next.photoKey,
                /**
                 * ⚠️ The entry's key, reused across retries rather than
                 * re-uploaded. A fresh upload would mint a new `garmentKey`, and
                 * the server's in-flight lock keys on it — so re-uploading to
                 * retry would defeat the double-tap protection entirely.
                 */
                garmentKey: next.target.garmentKey,
                category: next.target.category,
                name: next.target.name,
                /**
                 * ⚠️ **Matters more on this arm than on the catalogue one.**
                 * Without it the server writes no row, and an ad-hoc generation
                 * with no row cannot be recovered at all — the key, category and
                 * name above exist nowhere else once this request is over, so
                 * losing them means re-photographing the garment and paying
                 * again.
                 */
                visitId: currentVisitId,
              });

        if (response.success) {
          /**
           * ⚠️ **Rendered straight from the url, not from a blob.** The image
           * lives on FASHN's cdn and is renderable the moment the response
           * lands; building an object url first would put a second download in
           * front of the one moment the customer is actually waiting for.
           *
           * Nothing is pushed to `objectUrlsRef` here — a remote url is not an
           * object url and there is nothing to revoke. The copy restored from
           * IndexedDB on a later visit *is* one, and that path still tracks it.
           */
          const imageUrl = response.image;
          update(next.key, { status: 'done', imageUrl, failure: undefined });

          /**
           * Persistence is a separate best-effort step, fire-and-forget as it
           * was before — but the failure mode moved. It can now fail on the
           * *network* rather than only on IndexedDB, and the url is FASHN's
           * rather than ours: it expires after ~3 days, so a copy never taken is
           * a result the customer keeps seeing now and loses on reload.
           *
           * Swallowed on purpose. There is nothing to tell them — the image is
           * on screen, the generation was not wasted, and a toast about a
           * cache write is noise about a thing they never asked for.
           */
          void fetch(imageUrl)
            .then((res) => {
              // A 403 or 404 still resolves; without this the "blob" saved would
              // be an error page that renders as a broken image next visit.
              if (!res.ok) throw new Error(`result download failed: ${res.status}`);
              return res.blob();
            })
            .then((blob) =>
              saveResult({
                id: resultId(currentVisitId, next.photoKey, next.garmentId),
                visitId: currentVisitId,
                photoKey: next.photoKey,
                garmentId: next.garmentId,
                garmentName: next.garmentName,
                // Present on ad-hoc results only — the flag the rehydrate above
                // reads to tell the two kinds apart.
                ...(next.target.kind === 'adhoc' ? { adHocCategory: next.target.category } : {}),
                image: blob,
                createdAt: Date.now(),
              }),
            )
            .catch(() => {});
        } else if (response.reason === 'already_generating') {
          /**
           * ⚠️ **Not an error, and never shown as one.** It means the server's
           * in-flight lock (member + garment + photo) is held — so this exact
           * image is already being produced and text on the card would be
           * telling the customer off for something they did not do.
           *
           * With the `commit` fix above, this client can no longer race itself,
           * so reaching here means a genuine duplicate from another tab or
           * device. The card keeps spinning, and `STALE_LOCK_MS` below is what
           * stops that spinner being forever.
           *
           * 🔒 Deliberately **not** auto-retried. The other holder's image goes
           * to the other holder, so re-issuing once the lock frees is a second
           * Gemini call — a real charge, made without anyone asking for it.
           */
          update(next.key, { status: 'generating', failure: undefined });
          scheduleStaleLockCheck(next.key);
        } else {
          const presentation = presentReason(response.reason);
          update(next.key, { status: 'failed', failure: presentation });
        }
      } catch {
// Business failures arrive as 200s with `{ success: false }`, so
        // anything thrown here is transport: the app was backgrounded, wifi
        // dropped, or iOS purged the page. The generation may still have
        // succeeded server-side and been billed — nothing there aborts on a
        // client disconnect.
        //
        // ⚠️ **Recoverable rather than lost, provided a `visitId` went with the
        // request.** The row is settled server-side regardless, so the next
        // mount's recovery read fills this card in for free. The failure is
        // shown anyway because that read may be minutes away, or may never
        // happen on this device — and a card that silently waits is worse than
        // one offering a retry.
        update(next.key, { status: 'failed', failure: TRANSPORT_FAILURE });
      } finally {
        runningRef.current -= 1;
        endCriticalWork();
        runNext();
      }
    })();

    // Fill remaining slots in the same tick.
    runNext();
  }, [update]);

  const start = useCallback(
    (target: GenerationTarget) => {
      const currentPhotoKey = photoKeyRef.current;
      if (!currentPhotoKey) return;

      const subject = subjectOf(target);
      const key = entryKey(currentPhotoKey, subject);
      const existing = entriesRef.current.find((entry) => entry.key === key);
      // A completed or running card is not re-queued by another tap — that is
      // the double-tap the server-side lock exists to refuse. Scoped to this
      // photo, so the same garment on a retaken photo is a fresh generation.
      //
      // ⚠️ On the ad-hoc arm this only catches a repeat of the *same upload*.
      // Photographing the same physical garment twice mints a second
      // `garmentKey`, so it is a different subject here and a different lock
      // key on the server — two generations, both billed. That is correct:
      // they are genuinely two different images.
      if (existing && existing.status !== 'failed') return;

      const queued: GenerationEntry = {
        key,
        garmentId: subject,
        garmentName: target.name,
        photoKey: currentPhotoKey,
        target,
        status: 'queued',
      };

      /**
       * ⚠️ **Replaced in place, not moved to the end.** Re-generating after a
       * failure used to drop the old card and append a new one, so the card
       * jumped to the far end of the rail — off-screen on a phone — and the
       * customer watched an empty space where their garment had been.
       */
      commit(
        existing
          ? entriesRef.current.map((entry) => (entry.key === key ? queued : entry))
          : [...entriesRef.current, queued],
      );

      // `commit` is synchronous, so the queued entry is already visible to
      // `runNext` — but keep the microtask so the render lands first.
      queueMicrotask(runNext);
    },
    [commit, runNext],
  );

  const retry = useCallback(
    (key: string) => {
      const entry = entriesRef.current.find((item) => item.key === key);
      if (!entry || entry.status !== 'failed') return;
      update(key, { status: 'queued', failure: undefined });
      queueMicrotask(runNext);
    },
    [runNext, update],
  );

  const reset = useCallback(() => {
    for (const url of objectUrlsRef.current) URL.revokeObjectURL(url);
    objectUrlsRef.current = [];
    for (const timer of staleTimersRef.current.values()) clearTimeout(timer);
    staleTimersRef.current.clear();
    commit([]);
  }, [commit]);

  const entryFor = useCallback((garmentId: string) => {
    const currentPhotoKey = photoKeyRef.current;
    if (!currentPhotoKey) return undefined;
    const key = entryKey(currentPhotoKey, garmentId);
    return entriesRef.current.find((entry) => entry.key === key);
  }, []);

  // Revoke object URLs on unmount so a long session does not leak blobs.
  useEffect(() => {
    return () => {
      for (const url of objectUrlsRef.current) URL.revokeObjectURL(url);
      objectUrlsRef.current = [];
    };
  }, []);

  const busy = entries.some((entry) => entry.status === 'generating' || entry.status === 'queued');

  return { entries, busy, start, retry, reset, entryFor };
}
